import React, { useEffect } from 'react';
import { Select, SelectProps, SpaceBetween, TextFilter } from '@amzn/awsui-components-react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserPrefs as selectUserPrefsInStore, setSavedSearches as setSavedSearchesInStore } from '../stores/slices/userSlice';
import SaveSearch from './SaveSearch';
import DeleteSearch from './DeleteSearch';
import { debug } from '../utils/commonUtils';
import { setInputsSearch, selectInputsSearch, Search } from 'src/stores/slices/devicesSlice';

export default function InputsFilter(props: any) {
  debug(`InputsFilter() props is ${JSON.stringify(props)}`);

  const dispatch = useDispatch();

  const selectUserPrefs = useSelector(selectUserPrefsInStore);
  const [ userPrefs, setUserPrefs ] = React.useState(selectUserPrefs);

  const inputsSearch = useSelector(selectInputsSearch) as Search;
  debug(`InputsFilter() inputsSearch is ${JSON.stringify(inputsSearch)}`);

  const [ filteringText, setFilteringText ] = React.useState<string>(inputsSearch?.textFilter || '');

  const [ filterInputsStatus, setFilterInputsStatus ] = React.useState<any>(
    inputsSearch?.maskStatus ||
    {
      label: 'Any Status',
      value: 'Any Status'
    });

  const [ selectedInputsSavedSearch, setSelectedInputsSavedSearch ] = React.useState<{label: string, value: string}>(
    inputsSearch?.savedSearch ||
    {
      label: 'Saved Searches',
      value: 'Saved Searches'
    });

  const [ showSaveSearch, setShowSaveSearch ] = React.useState(false);
  const [ showDeleteSearch, setShowDeleteSearch ] = React.useState(false);

  const [ savedSearchesOptions, setSavedSearchesOptions ] = React.useState<SelectProps.Options>();

  const resetInputsFilters = () => {
    debug(`InputsFilter() resetInputsFilters()`);
    setFilteringText('');
    setFilterInputsStatus( { label: 'Any Status', value: 'Any Status' } );
    setSelectedInputsSavedSearch( { label: 'Saved Searches', value: 'Saved Searches' } );
    props.setFilterTextCallback('');
    props.resetSelectedItemsCallback();
    dispatch(setInputsSearch(
      {
        maskStatus: { label: 'Any Status', value: 'Any Status' },
        modes: [],
        savedSearch: undefined,
        textFilter: ''
      }
    ));
  };

  const setFilterInputsStatusOnChangeHandler = (detail: SelectProps.ChangeDetail) => {
    debug(`InputsFilter() setFilterInputsStatusOnChangeHandler()`);
    setFilterInputsStatus(detail.selectedOption);
    props.resetSelectedItemsCallback();
  };

  const savedSearch = (searchName: string) => {
    debug(`InputsFilter() savedSearch() searchName is ${searchName}`);
    let savedSearches = userPrefs.inputs?.savedSearches || [];
    debug(`InputsFilter() savedSearch() savedSearches is ${JSON.stringify(savedSearches)}`);
    const newSavedOption = { label: searchName, value: searchName };
    debug(`InputsFilter() savedSearch() newSavedOption is ${JSON.stringify(newSavedOption)}`);
    setSelectedInputsSavedSearch(newSavedOption);
    dispatch(setSavedSearchesInStore(
      {
        devicesType: 'inputs',
        savedSearches: [
          ...savedSearches,
          {
            name: searchName,
            filterText: filteringText,
            maskStatus: filterInputsStatus.value,
            modes: []
          }
        ]
      }));
  };

  const deletedSearch = (searchName: string) => {
    debug(`deletedSearch() searchName is ${searchName}`);
    let savedSearches = userPrefs.inputs?.savedSearches || [];
    debug(`deletedSearch() savedSearches is ${JSON.stringify(savedSearches)}`);
    let filteredSearches = savedSearches.filter((ss: { name: string; filterText: string; maskStatus: string; }) => ss.name !== searchName);
    debug(`deletedSearch() filteredSearches is ${JSON.stringify(filteredSearches)}`);
    resetInputsFilters();
    dispatch(setSavedSearchesInStore(
      {
        devicesType: 'inputs',
        savedSearches: filteredSearches
      }));
  };

  const savedSearchesOnChange = (detail: SelectProps.ChangeDetail) => {
    const value = detail.selectedOption.value;
    const label = detail.selectedOption.label;
    const chosenSavedSearch = {label: label!, value: value!};
    debug(`InputsFilter() savedSearchesOnChange() chosenSavedSearch is ${chosenSavedSearch}`);
    const actions = ['Reset','Save Search','Delete Search'];

    if (value === 'Reset') resetInputsFilters();
    if (value === 'Save Search') setShowSaveSearch(true);
    if (value === 'Delete Search') setShowDeleteSearch(true);
    if (!actions.includes(value!)) {
      setSelectedInputsSavedSearch(chosenSavedSearch);
      const savedSearch = userPrefs.inputs.savedSearches.find(
        (ss: { name: string, filterText: string, maskStatus: string }) => {
          return ss.name === label;
        }
      );
      debug(`InputsFilter() savedSearchesOnChange() savedSearch is ${JSON.stringify(savedSearch)}`);
      props.resetSelectedItemsCallback();
      dispatch(setInputsSearch(
        {
          maskStatus: savedSearch.maskStatus,
          modes: savedSearch.modes,
          savedSearch: chosenSavedSearch,
          textFilter: savedSearch.filterText
        }
      ));
      setFilterInputsStatus({ label: savedSearch.maskStatus, value: savedSearch.maskStatus });
      setFilteringText(savedSearch.filterText)
      props.setFilterTextCallback(savedSearch.filterText);
    }
  };

  const getFilterCounterText = (count: number) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  useEffect(() => {
    debug(`InputsFilter() useEffect() [selectUserPrefs]`)
    const updatedUserPrefs = selectUserPrefs;
    debug(`InputsFilter() useEffect() updatedUserPrefs is ${JSON.stringify(updatedUserPrefs)}`);
    let options =
      [
        { label: 'Saved Searches', value: 'Saved Searches', icon: 'filter', disabled: true },
        ...updatedUserPrefs.inputs.savedSearches.map(
          (ss: { name: string, filterText: string, maskStatus: string, modes: [] }) => {
            return {label: ss.name, value: ss.name }
          }
        ).sort(),
        { label: 'Actions', value: 'Actions', iconName: 'menu', disabled: true },
        { label: 'Reset', value: 'Reset', iconName: 'undo' },
        { label: 'Save Search', value: 'Save Search', iconName: 'file' },
        { label: '-------------', value: 'Separator', disabled: true },
        { label: 'Delete Search', value: 'Delete Search', iconName: 'close' }
      ];
    debug(`InputsFilter() useEffect() options is ${JSON.stringify(options)}`);
    setSavedSearchesOptions(options);
    setUserPrefs(updatedUserPrefs);
  }, [selectUserPrefs])

  useEffect(() => {
    debug(`InputsFilter() useEffect() [filteringText] filteringText is ${filteringText}}`);
    dispatch(setInputsSearch(
      {
        ...inputsSearch,
        textFilter: filteringText
      }
    ));
    props.setFilterTextCallback(filteringText);
  }, [filteringText]);

  useEffect(() => {
    debug(`InputsFilter() useEffect() [filterInputsStatus] filterInputsStatus is ${JSON.stringify(filterInputsStatus)}`);
    dispatch(setInputsSearch(
      {
        ...inputsSearch,
        maskStatus: filterInputsStatus
      }
    ));
  }, [filterInputsStatus]);

  return (
    <>
      <SpaceBetween size='xs' direction='horizontal'>
        <TextFilter
          filteringAriaLabel='Filter Input Devices'
          filteringPlaceholder='Filter Input Devices'
          countText={getFilterCounterText(props.filteredItemsCount === undefined ? 0: props.filteredItemsCount)}
          filteringText={filteringText}
          onChange={ ( { detail } ) => setFilteringText(detail.filteringText)}
        />
        <Select
          onChange={({ detail }) => setFilterInputsStatusOnChangeHandler(detail) }
          options={[
            { label: 'Any Status', value: 'Any Status' },
            { label: 'Masked', value: 'Masked' },
            { label: 'Unmasked', value: 'Unmasked' },
            { label: '-------------', value: 'Separator', disabled: true },
            { label: 'Unknown', value: 'Unknown' },
          ]}
          selectedAriaLabel='Selected'
          selectedOption={filterInputsStatus}
          expandToViewport
        />
        <Select
          onChange={({ detail }) => { savedSearchesOnChange(detail) }}
          options={savedSearchesOptions}
          selectedAriaLabel='Selected'
          selectedOption={selectedInputsSavedSearch}
          expandToViewport
        />
      </SpaceBetween>
      {showSaveSearch &&
        <SaveSearch
          showSaveSearch={showSaveSearch}
          setShowSaveSearchCallback={setShowSaveSearch}
          userPrefs={userPrefs}
          searchName={selectedInputsSavedSearch.label}
          filteringText={filteringText}
          filterInputsStatus={filterInputsStatus.value}
          filterReadersModes={[]}
          savedSearchCallback={savedSearch}
          devicesType='inputs'
        />
      }
      {showDeleteSearch &&
        <DeleteSearch
          showDeleteSearch={showDeleteSearch}
          setShowDeleteSearchCallback={setShowDeleteSearch}
          userPrefs={userPrefs}
          searchName={selectedInputsSavedSearch.label}
          deletedSearchCallback={deletedSearch}
          devicesType='inputs'
        />
      }
    </>);
}